import type { EdgeId, Graph, NodeId } from '../../../types/graph';
import { DeferredCompositeCommand } from '../composite';
import type { GraphCommand } from '../types';
import { RemoveEdgeCommand } from '../elementary';
import { RemoveNodeCommand } from '../elementary/remove-node';
import {
  deleteGraphsForRemovedNodes,
  restoreGraphsForAddedNodes,
  withEffects,
} from '../effects';

const recursiveRemove = (
  graph: Readonly<Graph>,
  rootNodeId: NodeId
): GraphCommand[] => {
  // Collect nodes to remove (root + all descendants).
  const toRemove = new Set<NodeId>();
  const queue: NodeId[] = [rootNodeId];

  // Helper: get children of a node via outgoing edges (from -> to).
  const getChildren = (g: Readonly<Graph>, nodeId: NodeId): NodeId[] => {
    const children: NodeId[] = [];
    for (const edgeId in g.edges) {
      const edge = g.edges[edgeId];
      if (edge.to.nodeId === nodeId) {
        children.push(edge.from.nodeId);
      }
    }
    return children;
  };

  // Helper: optional derivation expansion.
  // Adapt this to your actual structure. Examples:
  // - node.derivationIds: NodeId[]
  // - node.derivedFromId: NodeId
  // - graph.derivations[nodeId]: NodeId[]
  const getDerivationRelatedNodeIds = (
    g: Readonly<Graph>,
    nodeId: NodeId
  ): NodeId[] => {
    const node = g.nodes[nodeId];
    if (!node) return [];

    // Example shape; adjust/remove as needed.
    const derivationIds = (node as any).derivationIds as NodeId[] | undefined;
    return Array.isArray(derivationIds) ? derivationIds : [];
  };

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (toRemove.has(current)) continue;
    toRemove.add(current);

    // If current is a parent, its children are dependent and should be removed.
    const children = getChildren(graph, current);
    for (const child of children) {
      if (!toRemove.has(child)) queue.push(child);
    }

    // Also clear derivations (if applicable).
    const derivations = getDerivationRelatedNodeIds(graph, current);
    for (const d of derivations) {
      if (!toRemove.has(d)) queue.push(d);
    }
  }

  // Build commands:
  // 1) Remove all edges touching any node in toRemove
  // 2) Remove all nodes in toRemove
  const cmds: GraphCommand[] = [];
  const removedEdges = new Set<EdgeId>();

  for (const edgeId in graph.edges) {
    const edge = graph.edges[edgeId];
    if (toRemove.has(edge.from.nodeId) || toRemove.has(edge.to.nodeId)) {
      if (!removedEdges.has(edge.id)) {
        removedEdges.add(edge.id);
        cmds.push(new RemoveEdgeCommand(edge.id));
      }
    }
  }

  // Remove nodes after edges (often safer, depending on your reducer invariants).
  for (const nodeId of toRemove) {
    cmds.push(new RemoveNodeCommand(nodeId));
  }

  return cmds;
};

/**
 * Removing a node can have 2 cases in the current graph:
 * (1) The node is a child of another node
 *      -> remove node + edge.
 * (2) The node is both a parent of a node and a child of another node
 *      -> remove node + edge + all elements that are dependent on this node.
 *
 * Furthermore, a node can have derivations, when removing the node these derivations should also be cleared.
 * (TODO! Do we want to handle this in the elementary command?)
 */
export const createRemoveNodeCommand = (nodeId: NodeId) => {
  return withEffects(
    new DeferredCompositeCommand(
      (graph) => recursiveRemove(graph, nodeId),
      'Remove node'
    ),
    ({ before, after }) => deleteGraphsForRemovedNodes(before, after),
    ({ inverse }) =>
      withEffects(inverse, ({ before, after }) =>
        restoreGraphsForAddedNodes(before, after)
      )
  );
};
